// Colour conversion helpers shared by the onboarding handler and the
// branding editor in the dashboard. RTDB stores colours as `rgb(r,g,b)`
// strings (iOS parity) while <input type="color"> uses hex - these helpers
// bridge the two formats.

// Normalises a colour to the canonical RTDB format: `rgb(r,g,b)` with no
// spaces (iOS parity), e.g. `rgb(193,68,66)`. Accepts:
//   - `#rrggbb` / `rrggbb` / `#rgb` / `rgb` hex (case-insensitive)
//   - `rgb(...)` / `rgba(...)` with any spacing (alpha is dropped)
// Anything unrecognised (e.g. a CSS keyword) passes through untouched.
export const hexToRgb = (value) => {
  if (typeof value !== "string") return value;
  const input = value.trim();

  // 6-digit hex.
  const hex6 = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(input);
  if (hex6) {
    return `rgb(${parseInt(hex6[1], 16)},${parseInt(hex6[2], 16)},${parseInt(hex6[3], 16)})`;
  }

  // 3-digit shorthand hex (#abc -> #aabbcc).
  const hex3 = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(input);
  if (hex3) {
    const expand = (c) => parseInt(c + c, 16);
    return `rgb(${expand(hex3[1])},${expand(hex3[2])},${expand(hex3[3])})`;
  }

  // rgb()/rgba() in any spacing - re-emit in the canonical no-space form,
  // clamped to 0-255 and with any alpha channel dropped.
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+\s*)?\)$/i.exec(input);
  if (rgb) {
    const clamp = (n) => Math.max(0, Math.min(255, parseInt(n, 10)));
    return `rgb(${clamp(rgb[1])},${clamp(rgb[2])},${clamp(rgb[3])})`;
  }

  return value;
};

// Accepts `rgb(r,g,b)` (with or without spaces) and returns `#rrggbb`.
// Returns the input unchanged if it's already a hex string or otherwise
// unrecognised, so the M5 branding editor can safely feed RTDB values
// straight into <input type="color">.
export const rgbToHex = (value) => {
  if (typeof value !== "string") return value;
  if (/^#[0-9a-f]{6}$/i.test(value)) return value.toLowerCase();
  const match = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i.exec(value);
  if (!match) return value;
  const toHex = (n) => {
    const clamped = Math.max(0, Math.min(255, parseInt(n, 10)));
    return clamped.toString(16).padStart(2, "0");
  };
  return `#${toHex(match[1])}${toHex(match[2])}${toHex(match[3])}`;
};
